"use client"

import Link from "next/link"
import { ChevronDown, Search, X } from "lucide-react"
import { useEffect, useMemo, useState } from "react"

import { MotionSection } from "@/components/motion"
import { Input } from "@/components/ui/input"
import type { NewsEntry } from "@/components/source-page"

const TIMELINE_YEARS = Array.from({ length: 11 }, (_, index) => String(2026 - index))

type TimelineDate = {
  dateTime: string
  label: string
  slug: string
}

export type NewsArchiveEntry = Pick<NewsEntry, "slug" | "title" | "dateLabel" | "dateTime" | "image" | "excerpt" | "notice">

function formatTimelineDate(dateTime: string) {
  const date = new Date(`${dateTime}T12:00:00Z`)
  if (Number.isNaN(date.getTime())) return dateTime

  return new Intl.DateTimeFormat("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  }).format(date)
}

function getNewsSearchText(entry: NewsArchiveEntry) {
  return [entry.title, entry.dateLabel, entry.excerpt, entry.notice].filter(Boolean).join(" ").toLocaleLowerCase()
}

function buildTimeline(entries: NewsArchiveEntry[]) {
  const timeline = new Map<string, TimelineDate[]>()

  entries.forEach((entry) => {
    const dateTime = entry.dateTime
    const year = dateTime?.slice(0, 4)
    if (!dateTime || !year || !TIMELINE_YEARS.includes(year)) return

    const dates = timeline.get(year) ?? []
    if (!dates.some((date) => date.dateTime === dateTime)) {
      dates.push({
        dateTime,
        label: formatTimelineDate(dateTime),
        slug: entry.slug,
      })
    }
    timeline.set(year, dates)
  })

  timeline.forEach((dates) => {
    dates.sort((left, right) => right.dateTime.localeCompare(left.dateTime))
  })

  return timeline
}

export function NewsArchiveList({ entries }: { entries: NewsArchiveEntry[] }) {
  const [query, setQuery] = useState("")
  const [selectedYear, setSelectedYear] = useState("")
  const [selectedDate, setSelectedDate] = useState("")
  const [openYear, setOpenYear] = useState<string | null>(null)
  const [pendingScrollSlug, setPendingScrollSlug] = useState<string | null>(null)

  const timeline = useMemo(() => buildTimeline(entries), [entries])
  const filteredEntries = useMemo(() => {
    const normalizedQuery = query.trim().toLocaleLowerCase()

    return entries.filter((entry) => {
      const matchesQuery = !normalizedQuery || getNewsSearchText(entry).includes(normalizedQuery)
      const matchesYear = !selectedYear || entry.dateTime?.startsWith(selectedYear)
      const matchesDate = !selectedDate || entry.dateTime === selectedDate
      return matchesQuery && matchesYear && matchesDate
    })
  }, [entries, query, selectedDate, selectedYear])

  useEffect(() => {
    if (!pendingScrollSlug) return

    const target = document.getElementById(`news-entry-${pendingScrollSlug}`)
    if (!target) return

    const frame = window.requestAnimationFrame(() => {
      target.scrollIntoView({ behavior: "smooth", block: "start" })
      setPendingScrollSlug(null)
    })

    return () => window.cancelAnimationFrame(frame)
  }, [filteredEntries, pendingScrollSlug])

  const clearFilters = () => {
    setQuery("")
    setSelectedYear("")
    setSelectedDate("")
    setOpenYear(null)
    setPendingScrollSlug(null)
  }

  const handleYearSelect = (year: string, hasDates: boolean) => {
    if (!hasDates) return

    const nextYear = selectedYear === year && !selectedDate ? "" : year
    setSelectedYear(nextYear)
    setSelectedDate("")
    setOpenYear(nextYear ? year : null)
    setPendingScrollSlug(null)
  }

  const handleDateSelect = (year: string, date: TimelineDate) => {
    setSelectedYear(year)
    setSelectedDate(date.dateTime)
    setOpenYear(year)
    setPendingScrollSlug(date.slug)
  }

  const hasFilters = Boolean(query.trim() || selectedYear || selectedDate)

  return (
    <section className="source-blog-section source-news-archive" aria-label="News archive">
      <div className="source-blog-layout">
        <div className="source-blog-main source-news-main">
          <div className="source-blog-search-row">
            <label className="source-blog-search-label" htmlFor="news-search">
              <span className="source-blog-search-title">Search</span>
              <span className="source-blog-search-wrap">
                <Input
                  id="news-search"
                  type="search"
                  aria-label="Search News & Events"
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                  placeholder="Search News & Events"
                  className="source-blog-search-input"
                />
                {query ? (
                  <button
                    type="button"
                    className="source-blog-search-action"
                    aria-label="Clear News & Events search"
                    onClick={() => setQuery("")}
                  >
                    <X aria-hidden="true" size={17} strokeWidth={2} />
                  </button>
                ) : (
                  <Search aria-hidden="true" className="source-blog-search-icon" size={17} strokeWidth={1.8} />
                )}
              </span>
            </label>
          </div>

          {hasFilters ? (
            <div className="source-news-results" role="status" aria-live="polite">
              <span>
                Showing {filteredEntries.length} {filteredEntries.length === 1 ? "update" : "updates"}
                {selectedYear ? ` from ${selectedYear}` : ""}
              </span>
              <button type="button" className="source-blog-reset" onClick={clearFilters}>Clear filters</button>
            </div>
          ) : null}

          {filteredEntries.length ? (
            <div className="source-news-collection" aria-label="News updates">
              {filteredEntries.map((entry) => (
                <div key={entry.slug} id={`news-entry-${entry.slug}`} className="source-news-entry-shell">
                  {entry.notice ? <p className="source-news-entry-kicker">{entry.notice}</p> : null}
                  <MotionSection className="source-news-entry-card" whileHover={{ y: -6 }} whileTap={{ scale: 0.995 }}>
                    <div className="source-news-entry-media">
                      <time className="source-news-entry-date" dateTime={entry.dateTime}>{entry.dateLabel}</time>
                      {entry.image ? (
                        <Link href={`/news/${entry.slug}/`} className="source-news-entry-image-link" aria-label={`Read ${entry.title}`}>
                          <img
                            className="source-news-entry-image"
                            src={entry.image.src}
                            alt={entry.image.alt || entry.title}
                            loading="lazy"
                            decoding="async"
                            referrerPolicy="no-referrer"
                          />
                        </Link>
                      ) : null}
                    </div>
                    <div className="source-news-entry-content">
                      <h3 className="source-news-entry-heading">
                        <Link href={`/news/${entry.slug}/`}>{entry.title}</Link>
                      </h3>
                      <p className="source-news-entry-excerpt">{entry.excerpt}</p>
                      <Link className="source-news-entry-link" href={`/news/${entry.slug}/`}>Read More</Link>
                    </div>
                  </MotionSection>
                </div>
              ))}
            </div>
          ) : (
            <div className="source-blog-empty" role="status">
              <strong>No news updates match those filters.</strong>
              <button type="button" className="source-blog-reset" onClick={clearFilters}>Clear filters</button>
            </div>
          )}
        </div>

        <aside className="source-blog-sidebar source-news-sidebar" aria-label="News timeline">
          <section className="source-blog-sidebar-section source-news-timeline" aria-labelledby="news-timeline-title">
            <span className="source-blog-sidebar-kicker">News &amp; Events</span>
            <h2 id="news-timeline-title">Timeline</h2>
            <ul className="source-news-timeline-list">
              {TIMELINE_YEARS.map((year) => {
                const dates = timeline.get(year) ?? []
                const isOpen = openYear === year || selectedYear === year
                const isActive = selectedYear === year

                return (
                  <li
                    key={year}
                    className={`source-news-timeline-item${isActive ? " is-active" : ""}`}
                    data-open={isOpen ? "true" : "false"}
                    onMouseEnter={() => {
                      if (dates.length) setOpenYear(year)
                    }}
                    onMouseLeave={() => setOpenYear((current) => current === year ? null : current)}
                    onFocus={(event) => {
                      if (event.currentTarget.contains(event.relatedTarget as Node)) return
                      if (dates.length) setOpenYear(year)
                    }}
                    onBlur={(event) => {
                      if (event.currentTarget.contains(event.relatedTarget as Node)) return
                      setOpenYear((current) => current === year ? null : current)
                    }}
                  >
                    <button
                      type="button"
                      className="source-news-timeline-year"
                      aria-controls={dates.length ? `news-timeline-${year}` : undefined}
                      aria-expanded={dates.length ? isOpen : undefined}
                      aria-label={dates.length ? `Filter news updates from ${year}` : `${year}, no published news`}
                      disabled={!dates.length}
                      onClick={() => handleYearSelect(year, dates.length > 0)}
                    >
                      <span>{year}</span>
                      {dates.length ? <ChevronDown aria-hidden="true" className="source-news-timeline-chevron" size={17} strokeWidth={1.8} /> : null}
                    </button>
                    {dates.length ? (
                      <ul id={`news-timeline-${year}`} className="source-news-timeline-dates" aria-label={`News dates in ${year}`}>
                        {dates.map((date) => (
                          <li key={date.dateTime}>
                            <button
                              type="button"
                              className={`source-news-timeline-date${selectedDate === date.dateTime ? " is-active" : ""}`}
                              aria-pressed={selectedDate === date.dateTime}
                              onClick={() => handleDateSelect(year, date)}
                            >
                              <span>{date.label}</span>
                              <span aria-hidden="true">↗</span>
                            </button>
                          </li>
                        ))}
                      </ul>
                    ) : null}
                  </li>
                )
              })}
            </ul>
          </section>
        </aside>
      </div>
    </section>
  )
}
